// The ServiceTitan move's photos (migration.linkQueue): files the pipeline couldn't tie to a job with
// confidence. Link one to its proposed job or another candidate (migration.linkAttachment), accept
// every single-candidate proposal at or above a confidence (migration.acceptLinks, at least 80%,
// after a sheet listing what it links), keep
// one that belongs to no job with a reason (migration.markUnlinkable), or put a decision back
// (migration.undoLink).

import { useState } from 'react';
import { client } from '../api.ts';
import { Empty, Loading, Panel, errorText, useQuery } from '../books/ui.tsx';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import { SaveButton } from '../shared/SaveButton.tsx';
import { useDesign } from '../blueprint/design.ts';
import {
  type PartProps,
  acceptable,
  equipmentText,
  holds,
  LINK_TABS,
  MIN_CONFIDENCE,
  OPERATE_PERMS,
  pct,
  RULE_LABEL,
  toAccept,
  type Link,
} from './model.ts';

export function Photos({ held, toast, timeZone }: PartProps) {
  const [status, setStatus] = useState<(typeof LINK_TABS)[number]['status']>('review');
  const q = useQuery<{ links: Link[] }>('migration.linkQueue', { status });
  const may = holds(held, OPERATE_PERMS);
  const [min, setMin] = useState('0.9');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState<Link | null>(null);
  const [confirming, setConfirming] = useState(false);
  const minN = Number(min);
  const minOk = Number.isFinite(minN) && minN >= MIN_CONFIDENCE && minN <= 1;
  const acceptAll = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ accepted: string[] }>('migration.acceptLinks', { minConfidence: minN });
      setConfirming(false);
      toast(
        r.accepted.length
          ? `Linked ${r.accepted.length} ${r.accepted.length === 1 ? 'photo' : 'photos'} to their proposed jobs. Each can be undone.`
          : 'None waiting met that confidence.',
      );
      await q.reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const taken = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
          new Date(iso),
        )
      : null;
  return (
    <Panel
      title="Photos"
      aside={
        <div className="pills" role="group" aria-label="Which photos">
          {LINK_TABS.map((t) => (
            <button
              key={t.status}
              type="button"
              className="pill"
              aria-pressed={status === t.status}
              onClick={() => setStatus(t.status)}
            >
              {t.label}
            </button>
          ))}
        </div>
      }
    >
      {status === 'review' && may && q.data && q.data.links.length > 0 && (
        <div className="row-actions mig-accept">
          <label className="inline-label">
            <span className="flabel">Accept every single-candidate proposal at or above</span>
            <select className="field" value={min} onChange={(e) => setMin(e.target.value)}>
              {['0.8', '0.85', '0.9', '0.95'].map((v) => (
                <option key={v} value={v}>
                  {pct(Number(v))}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn"
            disabled={!minOk || acceptable(q.data.links, minN) === 0}
            onClick={() => {
              setError(null);
              setConfirming(true);
            }}
          >
            Accept {acceptable(q.data.links, minN)} of {q.data.links.length}
          </button>
        </div>
      )}
      {!q.data ? (
        <Loading error={q.error} what="the photos" retry={() => void q.reload()} />
      ) : q.data.links.length === 0 ? (
        <Empty>{status === 'review' ? 'No photo is waiting for a person.' : 'Nothing here.'}</Empty>
      ) : (
        <ul className="rows mig-links">
          {q.data.links.map((l) => (
            <LinkRow
              key={l.fileId}
              link={l}
              may={may}
              taken={taken(l.capturedAt)}
              onUnlinkable={() => setUnlinking(l)}
              onChanged={async (said) => {
                toast(said);
                await q.reload();
              }}
            />
          ))}
        </ul>
      )}
      {confirming && q.data && (
        <AcceptAll
          links={toAccept(q.data.links, minN)}
          min={minN}
          busy={busy}
          error={error}
          onConfirm={() => void acceptAll()}
          onClose={() => setConfirming(false)}
        />
      )}
      {unlinking && (
        <Unlinkable
          link={unlinking}
          onClose={() => setUnlinking(null)}
          onDone={async () => {
            toast(`${unlinking.fileName ?? 'The file'} stays in the customer archive, with no job.`);
            setUnlinking(null);
            await q.reload();
          }}
        />
      )}
    </Panel>
  );
}

function LinkRow({
  link: l,
  may,
  taken,
  onUnlinkable,
  onChanged,
}: {
  link: Link;
  may: boolean;
  taken: string | null;
  onUnlinkable: () => void;
  onChanged: (said: string) => Promise<void>;
}) {
  const [pick, setPick] = useState(l.stJobId ?? l.candidates[0]?.stJobId ?? '');
  // Blueprint: the job and its Link it are one small form, and No job is the row's one action
  const bp = useDesign() === 'blueprint';
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (on: string, action: string, input: unknown, said: string) => {
    setBusy(on);
    setError(null);
    try {
      await client.call(action, input);
      await onChanged(said);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  const name = l.fileName ?? 'A file';
  return (
    <li className="mig-link">
      <div>
        <b>{name}</b>
        <span className="muted small block">
          {taken ? `Taken ${taken} · ` : ''}
          {RULE_LABEL[l.rule] ?? l.rule}
          {l.confidence ? ` · ${pct(l.confidence)} sure` : ''}
          {l.reason ? ` · ${l.reason}` : ''}
        </span>
        {/* what the photo is of, above the jobs to choose from */}
        {equipmentText(l.equipment) && (
          <span className="mig-equip small block">
            <b>Equipment:</b> {equipmentText(l.equipment)}
          </span>
        )}
        {l.status === 'review' && l.candidates.length > 0 && (
          <ul className="mig-cands small">
            {l.candidates.map((c) => (
              <li key={c.stJobId}>
                ServiceTitan job {c.stJobId}: {c.why}
              </li>
            ))}
          </ul>
        )}
        {l.status !== 'review' && l.stJobId && (
          <span className="small block">Linked to ServiceTitan job {l.stJobId}</span>
        )}
      </div>
      {may && l.status === 'review' && (
        <div className="mig-link-acts">
          {bp ? (
            <span className="mig-link-form" role="group" aria-label={`Link ${name} to a job`}>
              <label className="inline-label">
                <span className="flabel">Job</span>
                <input
                  className="field"
                  inputMode="numeric"
                  list={`cands-${l.fileId}`}
                  value={pick}
                  onChange={(e) => setPick(e.target.value.replace(/\D/g, ''))}
                />
                <datalist id={`cands-${l.fileId}`}>
                  {l.candidates.map((c) => (
                    <option key={c.stJobId} value={c.stJobId}>
                      {c.why}
                    </option>
                  ))}
                </datalist>
              </label>
              <SaveButton
                className="btn"
                busy={busy === 'link'}
                busyLabel="Linking…"
                error={error}
                disabled={!pick}
                onClick={() =>
                  void run(
                    'link',
                    'migration.linkAttachment',
                    { fileId: l.fileId, stJobId: pick },
                    `${name} linked to ServiceTitan job ${pick}.`,
                  )
                }
              >
                Link it
              </SaveButton>
            </span>
          ) : (
            <>
              <label className="inline-label">
                <span className="flabel">Job</span>
                <input
                  className="field"
                  inputMode="numeric"
                  list={`cands-${l.fileId}`}
                  value={pick}
                  onChange={(e) => setPick(e.target.value.replace(/\D/g, ''))}
                />
                <datalist id={`cands-${l.fileId}`}>
                  {l.candidates.map((c) => (
                    <option key={c.stJobId} value={c.stJobId}>
                      {c.why}
                    </option>
                  ))}
                </datalist>
              </label>
              <SaveButton
                busy={busy === 'link'}
                busyLabel="Linking…"
                error={error}
                disabled={!pick}
                onClick={() =>
                  void run(
                    'link',
                    'migration.linkAttachment',
                    { fileId: l.fileId, stJobId: pick },
                    `${name} linked to ServiceTitan job ${pick}.`,
                  )
                }
              >
                Link it
              </SaveButton>
            </>
          )}
          <button type="button" className="btn quiet" onClick={onUnlinkable}>
            No job
          </button>
        </div>
      )}
      {may && (l.status === 'linked' || l.status === 'unlinkable' || l.status === 'auto') && (
        <SaveButton
          busy={busy === 'undo'}
          busyLabel="Putting it back…"
          error={error}
          className="btn"
          onClick={() =>
            void run(
              'undo',
              'migration.undoLink',
              { fileId: l.fileId },
              `${name} is back with the photos waiting.`,
            )
          }
        >
          Undo
        </SaveButton>
      )}
    </li>
  );
}

function Unlinkable({
  link,
  onClose,
  onDone,
}: {
  link: Link;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keep = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('migration.markUnlinkable', { fileId: link.fileId, reason: reason.trim() });
      await onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title="Keep it with no job?"
      outcome={`${link.fileName ?? 'This file'} stays in the customer archive with the reason, and doesn’t count as missing.`}
      confirm="Keep it with no job"
      ready={!!reason.trim()}
      busy={busy}
      error={error}
      onConfirm={() => void keep()}
      onClose={onClose}
    >
      <label>
        Why (required)
        <input
          className="field"
          maxLength={300}
          value={reason}
          placeholder="A truck photo, a duplicate, nobody can place it"
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
    </MoneySheet>
  );
}

function AcceptAll({
  links,
  min,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  links: Link[];
  min: number;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const n = links.length;
  const shown = links.slice(0, 8);
  return (
    <MoneySheet
      title={`Link ${n} ${n === 1 ? 'photo' : 'photos'} to ${n === 1 ? 'its' : 'their'} proposed ${n === 1 ? 'job' : 'jobs'}?`}
      outcome={`Each goes to the one job proposed for it, at ${pct(min)} sure or more. Each one can be put back from “Linked by a person”.`}
      confirm={`Link ${n}`}
      ready={n > 0}
      busy={busy}
      error={error}
      onConfirm={onConfirm}
      onClose={onClose}
    >
      <ul className="small mig-accept-list">
        {shown.map((l) => (
          <li key={l.fileId}>
            {l.fileName ?? 'A file'} → ServiceTitan job {l.stJobId} ({pct(l.confidence)})
          </li>
        ))}
        {n > shown.length && <li className="muted">and {n - shown.length} more</li>}
      </ul>
    </MoneySheet>
  );
}
